import { Injectable } from '@angular/core';
import { IFromToDateObj, THoursFormat } from '@services/common/types/time.model';
import { differenceInDays, formatDistanceStrict, format, setHours, setMinutes, setSeconds, addSeconds } from 'date-fns';

import moment from 'moment';

import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

@Injectable({ providedIn: 'root' })
export class TimeService {
  secondsToHour = 3600 as const;
  minutesToHour = 60 as const;
  millisecondsToSeconds = 1000 as const;

  defaultDateFormat = 'DD.MM.YYYY';
  uiDateFormat = this.defaultDateFormat;

  /**
   * Change date format
   * @param {string | date} date - date to change format
   * @param {string} dateFormat - new date format default format is 'YYYY-MM-DD'
   * @return {string}
   */
  formatDate(date: Date | string, dateFormat = 'YYYY-MM-DD'): string {
    return format(legacyParse(date), convertTokens(dateFormat));
  }

  /**
   * @param {string} datetime - string in format 'YYYY-MM-DD HH:mm:ss'
   * @param {number} timezone - timezone for report
   * @returns {Object.<string, string>} datetime - formatted object of datetime,
   * {string} datetime.date - format in uiDateFormat constant
   * {string} datetime.time - 'HH:mm'
   * */
  parseDateTimeString(datetime, timezone = null, f = this.uiDateFormat) {
    if (typeof timezone === 'undefined' || timezone === null) {
      timezone = moment().utcOffset();
    }
    const date = datetime ? moment.utc(datetime).utcOffset(timezone).format(f) : '',
      time = datetime ? moment.utc(datetime).utcOffset(timezone).format('HH:mm') : '';

    return {
      date,
      time
    };
  }

  /**
   * Compare two dates and return is first grater then second
   * @param {string | date} grater
   * @param {string | date} than
   * @return {boolean}
   */
  greaterThen(grater: string, than: string): boolean {
    return moment(grater).diff(moment(than)) > 0;
  }

  dateEqual(date1: string | Date, date2: string | Date): boolean {
    return moment(date1).diff(moment(date2)) === 0;
  }

  toTimeZone(date, timezone) {
    const periodString = timezone.slice(3);
    let symbol = '+';
    let period = '';

    if (periodString.length) {
      symbol = periodString.substring(0, 1);
      period = periodString.slice(1);
    }

    let seconds = 0;

    if (period.length === 5) {
      seconds = this.periodToSeconds(period) * 60;
    }

    if (symbol === '-') {
      return addSeconds(legacyParse(date), -seconds);
    }

    return addSeconds(legacyParse(date), seconds);
  }

  /**
   *  @param {string|number} time
   *  @return {number} HH:mm to mm - 'mm'
   * */
  timeToMinutes(time: any): any {
    if (!time) {
      return time;
    }

    if (time instanceof Date) {
      time = this.formatTime(time);
    }

    const hours = time.substring(0, 2),
      minutes = time.substring(3, 5);

    return hours * 60 + minutes * 1;
  }

  /**
   *  @param {string} time hh:mm
   *  @return {number} Date
   * */
  timeToDate(time): Date {
    const [hours, minutes] = time.split(':');
    let newDate = setHours(legacyParse(new Date()), hours);
    newDate = setMinutes(legacyParse(newDate), minutes);
    newDate = setSeconds(legacyParse(newDate), 0);
    return newDate;
  }

  getWorkingDays(totalMinutes, defaultWorkingDayInMinutes) {
    const quotient = (totalMinutes - (totalMinutes % defaultWorkingDayInMinutes)) / defaultWorkingDayInMinutes;
    const remainder = totalMinutes % defaultWorkingDayInMinutes;

    return {
      days: quotient || 0,
      remainder: isNaN(remainder) ? '00:00' : this.minutesToTime(remainder)
    };
  }

  /**
   * @param {string} time - hours-minutes string
   * @return {string} formattedTime - hours-minutes string but with right format (e.g. '13:0' -> '13:00')
   * */
  formatTime(time) {
    if (time) {
      return this.formatDate(time, 'HH:mm');
    }
    return time;
  }

  /**
   * @param {string} fromTime - string in format 'YYYY-MM-DD HH:mm:ss'
   * @param {number} timeDuration - duration in seconds
   * @param {number} timezone
   * @returns {Object.<string, string>} datetime - formatted object of datetime,
   * */
  getToTimeDate(fromTime, timeDuration, timezone = null) {
    // Replaced from old app
    const defaultDateFormat = 'DD.MM.YYYY';
    if (typeof timezone === 'undefined' || timezone === null) {
      timezone = moment().utcOffset();
    }

    const toDateTime = fromTime ? moment.utc(fromTime).utcOffset(timezone).add(timeDuration, 'seconds') : null;

    const date = toDateTime ? toDateTime.format(defaultDateFormat) : '',
      time = toDateTime ? toDateTime.format('HH:mm') : '';

    return {
      date,
      time
    };
  }

  /**
   *  @param {string|number} minutes
   *  @return {number} minutes to seconds - 'ss'
   * */
  secondsToMinutes(seconds) {
    // Replaced from old app
    return seconds / this.minutesToHour;
  }

  secondsToHours(seconds) {
    return seconds / this.secondsToHour;
  }

  hoursToSeconds(hours) {
    return hours * this.secondsToHour;
  }

  /**
   *  @param {string|number} minutes
   *  @return {number} minutes to seconds - 'ss'
   * */
  minutesToSeconds(minutes) {
    return minutes * 60;
  }

  /**
   *  @param {string|number} seconds
   *  @return {string} periodLength - period length - 'HH:mm'
   * */
  secondsToPeriod(seconds) {
    // Replaced from old app
    let positive = null;

    if (seconds < 0) {
      positive = '-';
      seconds = Math.abs(seconds);
    }

    let hours: string | number = Math.floor(Number(seconds) / this.secondsToHour),
      minutes: string | number = Math.round((seconds - hours * this.secondsToHour) / this.minutesToHour);

    if (minutes == 60) {
      hours += 1;
      minutes = 0;
    }

    hours = hours.toString();
    minutes = minutes.toString();

    if (hours.length < 2) {
      hours = '0' + hours;
    }
    if (minutes.length < 2) {
      minutes = '0' + minutes;
    }

    if (positive !== null) {
      return `-${hours}:${minutes}`;
    }

    return hours + ':' + minutes;
  }

  periodToSeconds(period): number {
    // const fromData = period.match(/\d{1,2}/g);
    const fromData = period.split(':');

    const hours = +fromData[0];
    const minutes = +fromData[1];
    const seconds = 0;

    return seconds ? hours * 3600 + minutes * 60 + seconds : hours * 60 + minutes;
  }

  /**
   *  @param {string} from - time in format 'HH:mm'
   *  @param {string} to - time in format 'HH:mm'
   *  @return {number} diff - milliseconds value with difference
   * */
  getPeriodDiffValue(from, to, positive?) {
    if (from && to) {
      const fromData = from.match(/\d{1,2}/g),
        toData = to.match(/\d{1,2}/g),
        fromObj = {
          hours: +fromData[0],
          minutes: +fromData[1]
        },
        // tslint:disable-next-line:prefer-const
        toObj = {
          hours: +toData[0],
          minutes: +toData[1]
        },
        minutes = toObj.minutes - fromObj.minutes;
      let hours = toObj.hours - fromObj.hours;

      if (positive) {
        if (hours < 0) {
          // impossible outcome ?
          hours = 24 + hours;
        }
      }

      return (hours * this.secondsToHour + minutes * this.minutesToHour) * this.millisecondsToSeconds;
    }
    return 0;
  }

  /**
   *  @param {string|number} milliseconds
   *  @return {string} periodLength - period length - 'HH:mm'
   * */
  millisecondsToPeriod(milliseconds) {
    const seconds = milliseconds / this.millisecondsToSeconds;

    return this.secondsToPeriod(seconds);
  }

  /**
   *  @param {string|number} date
   *  @param {string} format
   *  @return {string} format - 'DD.MM.YYYY'
   * */

  getDateFormat(date, f = '') {
    if (f === '') {
      f = this.defaultDateFormat;
    }

    if (!date) {
      return '';
    }

    return format(legacyParse(date), convertTokens(f));
  }

  getLocalTimezone() {
    return moment().utcOffset() / this.minutesToHour;
  }

  getFromUtcFormat(date, f): string {
    return moment.utc(date).local().format(f);
  }

  /**
   *  @param {number} time
   *  @param {string} textFormat
   *  @return {string} HH:mm
   * */
  minutesToTime(time, textFormat?) {
    const isMinus = time < 0;
    if (isMinus) {
      time = time * -1;
    }
    let hours: string | number = (time - (time % this.minutesToHour)) / this.minutesToHour,
      minutes: any = (time % 60).toFixed(0);

    if (hours <= 9) {
      hours = '0' + hours;
    }
    if (minutes <= 9) {
      minutes = '0' + minutes;
    }
    if (textFormat) {
      return hours + 'h ' + minutes + 'm';
    }
    return '' + (isMinus ? '-' : '') + hours + ':' + minutes;
  }

  getPeriodDates(from, to) {
    if (!moment(from).isBefore(moment(to)) && from !== to) {
      return {
        days: [],
        daysObj: {}
      };
    }
    const toDate = to,
      days = [],
      daysObj = {};
    let currentDateForProcessing = from;

    while (moment(currentDateForProcessing).isSameOrBefore(toDate)) {
      const currentDateMomentObj = moment(currentDateForProcessing),
        dayNumberAtWeek = currentDateMomentObj.day(),
        dayNumberAtMonth = currentDateMomentObj.date(),
        weekNumber = currentDateMomentObj.isoWeek(),
        month = moment.monthsShort(currentDateMomentObj.month()),
        year = currentDateMomentObj.year(),
        dayNameFirstLetter = moment.weekdaysMin(dayNumberAtWeek);

      daysObj[currentDateForProcessing] = {
        number: dayNumberAtMonth,
        name: dayNameFirstLetter,
        dayNumber: dayNumberAtWeek,
        week: weekNumber,
        month,
        year: year,
        date: currentDateForProcessing
      };
      days.push({
        number: dayNumberAtMonth,
        name: dayNameFirstLetter,
        dayNumber: dayNumberAtWeek,
        week: weekNumber,
        year: year,
        month,
        date: currentDateForProcessing
      });
      currentDateForProcessing = currentDateMomentObj.add(1, 'days').format('YYYY-MM-DD');
    }
    return {
      days,
      daysObj
    };
  }

  /**
   *  @param {string} from - time in format 'HH:mm'
   *  @param {string} to - time in format 'HH:mm'
   *  @param {string} subtract - time in format 'HH:mm' that should be subtracted from period length
   *  @return {string} period - time in format 'HH:mm'
   * */
  getPeriodLengthString(from, to, subtract = '') {
    if (from && to) {
      const fromData = from.match(/\d{1,2}/g),
        toData = to.match(/\d{1,2}/g),
        fromObj = {
          hours: +fromData[0],
          minutes: +fromData[1]
        },
        toObj = {
          hours: +toData[0],
          minutes: +toData[1]
        },
        hours = toObj.hours - fromObj.hours,
        minutes = toObj.minutes - fromObj.minutes,
        resetObject = { year: 2000, month: 1, date: 1 };
      let momentObjectLength = moment().utcOffset(0);
      momentObjectLength.set({ hour: hours, minute: minutes });
      momentObjectLength.toISOString();
      momentObjectLength.format();

      if (momentObjectLength.set(resetObject).isBefore(moment.utc(subtract, 'HH:mm').set(resetObject))) {
        return '00:00';
      } else if (subtract) {
        const subtractData = subtract.match(/\d{1,2}/g);

        if (subtractData !== null) {
          const subtractObj = {
            hours: +subtractData[0],
            minutes: +subtractData[1]
          };

          momentObjectLength = momentObjectLength.subtract(subtractObj.hours, 'hour').subtract(subtractObj.minutes, 'minute');
        }
      }
      return momentObjectLength.format('HH:mm');
    }
    return '00:00';
  }

  /**
   *  @param {Date | Number} from - the later date
   *  @param {Date | Number} to - the earlier date
   *  @optional {'second' | 'minute' | 'hour' | 'day' | 'month' | 'year'} unit - if specified, will force a unit
   *  @return {Number} difference - the number of days
   * */
  periodDays(from, to) {
    return formatDistanceStrict(legacyParse(to), legacyParse(from), { unit: 'day' });
  }

  /**
   *  @param {Date | Number} from - the later date
   *  @param {Date | Number} to - the earlier date
   * */
  diffInDays(from, to) {
    return differenceInDays(legacyParse(to), legacyParse(from));
  }

  /**
   *  @param {string} dateString - date string in format uiDateFormat
   *  @return {string} dateString - date string in 'YYYY-MM-DD'
   * */
  formatDateStringForServer(dateString) {
    if (dateString) {
      return moment(dateString, this.defaultDateFormat).format('YYYY-MM-DD');
    }
    return '';
  }

  /**
   *  @param {string} dateString - string from server
   *  @return {string} dateString - string for UI(format in uiDateFormat constant)
   * */
  parseDateString(dateString) {
    if (dateString) {
      return moment(dateString).format(this.defaultDateFormat);
    }
    return '';
  }

  /**
   * @param {string} dateString - string in format 'YY-MM-DD'
   * @param {string} timeString - string in format 'HH:mm'
   * @param {number} timezone - timezone for report
   * @returns {Object.<string, string>} datetime - formatted object of datetime,
   * {string} datetime.date - format in uiDateFormat constant
   * {string} datetime.time - 'HH:mm'
   * */
  createDateTimeString(dateString: string, timeString: string, timezone: number): string {
    if (dateString && timeString) {
      if (typeof !!timezone) {
        timezone = moment().utcOffset();
      }
      return moment.utc(`${dateString} ${timeString}`, `YY-MM-DD HH:mm`).utcOffset(-timezone).format('YYYY-MM-DD HH:mm:ss');
    }
    return '';
  }

  /**
   * @param {string} time1 'HH:mm' addToTime
   * @param {string} time2 'HH:mm' addTime
   * @param {boolean} maxOneDay
   * @return {string}
   */
  sumTime(time1: string, time2: string, maxOneDay: boolean = false): THoursFormat {
    const t1 = moment.duration(time1);
    const t2 = moment.duration(time2);
    const sum = t1.add(t2);
    let hours = Math.trunc(sum.asHours());
    if (hours >= 24 && maxOneDay) {
      hours -= 24;
    }
    const minutes = sum.minutes();
    const result = (hours < 10 ? '0' + hours : hours) + ':' + (minutes < 10 ? '0' + minutes : minutes);
    return result as THoursFormat;
  }

  /**
   * @param {string} time1 'HH:mm'
   * @param {string} time2 'HH:mm'
   * @return {string} 'HH:mm' time
   */
  subtractTime(time1: string, time2: string): THoursFormat | '' {
    if (time2 > time1) {
      return '';
    }

    const t1 = moment.duration(time1);
    const t2 = moment.duration(time2);
    const sum = t1.subtract(t2);
    const hours = Math.trunc(sum.asHours());
    const minutes = sum.minutes();
    const result = (hours < 10 ? '0' + hours : hours) + ':' + (minutes < 10 ? '0' + minutes : minutes);
    return result as THoursFormat;
  }

  private containPeriod(firstPeriodArg, secondPeriodArg, hasBetweenCondition) {
    return hasBetweenCondition
      ? (firstPeriodArg.start.isSameOrBefore(secondPeriodArg.start) && firstPeriodArg.end.isSameOrAfter(secondPeriodArg.end)) ||
          firstPeriodArg.start.isBetween(secondPeriodArg.start, secondPeriodArg.end) ||
          firstPeriodArg.end.isBetween(secondPeriodArg.start, secondPeriodArg.end)
      : firstPeriodArg.start.isSameOrBefore(secondPeriodArg.start) && firstPeriodArg.end.isSameOrAfter(secondPeriodArg.end);
  }

  // checks only is first includes second or first starts or ends 'inside' of second !!!
  isTimePeriodsInclusivity(firstPeriodStart, firstPeriodEnd, secondPeriodStart, secondPeriodEnd, hasBetweenCondition = true) {
    const timeFormat = 'HH:mm',
      firstPeriod = {
        start: moment(firstPeriodStart, timeFormat),
        end: moment(firstPeriodEnd, timeFormat)
      },
      secondPeriod = {
        start: moment(secondPeriodStart, timeFormat),
        end: moment(secondPeriodEnd, timeFormat)
      };

    let periodContainTwoDays = false;
    let settingPeriodContainTwoDays = false;
    let valid = false;

    if (firstPeriod.end.isBefore(firstPeriod.start)) {
      firstPeriod.end.add(1, 'day');
      periodContainTwoDays = true;
    }

    if (secondPeriod.end.isBefore(secondPeriod.start)) {
      secondPeriod.end.add(1, 'day');
      settingPeriodContainTwoDays = true;
    }

    if (settingPeriodContainTwoDays) {
      if (periodContainTwoDays) {
        // ok
      } else {
        const newFirstPeriod = {
          start: moment(firstPeriodStart, timeFormat).add(1, 'day'),
          end: moment(firstPeriodEnd, timeFormat).add(1, 'day')
        };

        valid = this.containPeriod(newFirstPeriod, secondPeriod, hasBetweenCondition);
      }
    } else {
      if (periodContainTwoDays) {
        const newSecondPeriod = {
          start: moment(secondPeriodStart, timeFormat).add(1, 'day'),
          end: moment(secondPeriodEnd, timeFormat).add(1, 'day')
        };

        valid = this.containPeriod(firstPeriod, newSecondPeriod, hasBetweenCondition);
      } else {
        // ok
      }
    }

    return valid || this.containPeriod(firstPeriod, secondPeriod, hasBetweenCondition);
  }

  /**
   *  @param {string} fromArg - time in format 'HH:mm'
   *  @param {string} toArg - time in format 'HH:mm'
   *  @param {string} dateArg - date in uiDateFormat format
   *  @param {number} timezoneArg
   *  @return {Object<string, string>} period object - object with from and to values 'YYYY-MM-DD HH:mm:ss'
   * */
  createFromToDateTimeObject(fromArg: string, toArg: string, dateArg?: string, timezoneArg?: number): IFromToDateObj {
    const date = dateArg ? dateArg : moment().format(this.uiDateFormat);
    const timezone = timezoneArg ?? this.getLocalTimezone();
    const periodDiff = this.getPeriodDiffValue(fromArg, toArg);

    // add 1 day if toArg > fromArg
    const newDate = periodDiff < 0 ? this.addDay(date) : date;
    const to = this.createDateTimeString(moment(newDate, this.uiDateFormat).format('YY-MM-DD'), toArg, timezone);
    const from = this.createDateTimeString(moment(date, this.uiDateFormat).format('YY-MM-DD'), fromArg, timezone);
    return {
      from: from,
      to: to
    };
  }

  /**
   *  @param {string} date - date in uiDateFormat format
   *  @param {number} quantity - number of days to add default 1
   *  @return {string} newDate - date in uiDateFormat format
   * */
  addDay(date: string, quantity?: number): string {
    const defaultQuantity = 1;
    const amount = quantity ? quantity : defaultQuantity;

    return moment(date, this.uiDateFormat).add(amount, 'days').format(this.uiDateFormat);
  }

  /**
   * @param {string} firstPeriodStart
   * @param {string} firstPeriodEnd
   * @param {string} secondPeriodStart
   * @param {string} secondPeriodEnd
   * @return {boolean} isPeriodIncludeAnother
   * */
  isPeriodIncludeAnother(
    firstPeriodStart: string,
    firstPeriodEnd: string,
    secondPeriodStart: string,
    secondPeriodEnd: string
  ): boolean {
    const timeFormat = 'YYYY-MM-DD HH:mm:ss',
      firstPeriod = {
        start: moment(firstPeriodStart, timeFormat),
        end: moment(firstPeriodEnd, timeFormat)
      },
      secondPeriod = {
        start: moment(secondPeriodStart, timeFormat),
        end: moment(secondPeriodEnd, timeFormat)
      };

    return firstPeriod.start.isSameOrBefore(secondPeriod.start) && firstPeriod.end.isSameOrAfter(secondPeriod.end);
  }

  /**
   * @param {string} finishDate string in uiDateFormat
   * @return {string} days count
   * */
  leftDays(finishDate): number {
    const currentDate = moment().format('YYYY-MM-DD'),
      from = moment(currentDate, 'YYYY-MM-DD'),
      to = moment(moment(finishDate).format('YYYY-MM-DD'), 'YYYY-MM-DD');

    return to.diff(from, 'days');
  }

  /**
   * @param {to} to - date in format uiDateFormat
   * @param {from} from - date in format uiDateFormat
   * @return {number} difference in seconds
   * */
  getDatetimesDifferenceInSeconds(to: string, from: string): number {
    const toFormatted = moment.utc(to);
    const fromFormatted = moment.utc(from);

    return toFormatted.diff(fromFormatted, 'seconds');
  }

  /**
   * @param {string} timestamp
   * @return {string} date string in 'YYYY-MM-DD' format
   * */
  timeStampToDate(timestamp): string {
    return moment.unix(timestamp).format(this.defaultDateFormat);
  }
}
